import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import { getResendAvailability } from '@/services/apis/mfa-orchestrator.api';
import { logError } from '@/utils/error-logger';

const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

// A login flow lives minutes, so a deadline a day out is the never sentinel.
const RESEND_NEVER_THRESHOLD_MS = 24 * 60 * 60 * MS_PER_SECOND;

const EXHAUSTED = -1;
const READY = 0;

const parseDeadline = (deadline?: string) => {
  if (!deadline) {
    return null;
  }

  const parsed = new Date(deadline).getTime();

  return Number.isNaN(parsed) ? null : parsed;
};

// A snapshot that holds still while idle keeps the screen from re-rendering.
const readCountdown = (deadline: number | null) => {
  if (deadline === null) {
    return READY;
  }

  const remainingMs = deadline - Date.now();

  if (remainingMs > RESEND_NEVER_THRESHOLD_MS) {
    return EXHAUSTED;
  }

  return remainingMs <= 0 ? READY : Math.ceil(remainingMs / MS_PER_SECOND);
};

const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / SECONDS_PER_MINUTE);
  const remainingSeconds = seconds % SECONDS_PER_MINUTE;

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
};

const laterDeadline = (a?: string, b?: string) => {
  const parsedA = parseDeadline(a);
  const parsedB = parseDeadline(b);

  if (parsedA === null) {
    return b;
  }

  if (parsedB === null) {
    return a;
  }

  return parsedB > parsedA ? b : a;
};

// A re-executed step and a resend response each report a deadline, and they
// arrive in either order, so the later one is the one still standing.
export const useResendDeadline = (stepDeadline?: string) => {
  const [resendDeadline, setResendDeadline] = useState<string>();
  const [hasFailedRead, setHasFailedRead] = useState(false);
  const deadline = laterDeadline(stepDeadline, resendDeadline);

  // A submit response carries no step metadata and a cold load has no step
  // state, so with no deadline in hand the flow's budget is the only source.
  useEffect(() => {
    if (deadline) {
      return;
    }

    getResendAvailability()
      .then(fetched =>
        setResendDeadline(previous => laterDeadline(previous, fetched))
      )
      .catch(error => {
        logError(error, 'mfa:resend-availability');
        setHasFailedRead(true);
      });
  }, [deadline]);

  return {
    deadline,
    setDeadline: setResendDeadline,
    // No deadline in hand is an unread budget, not an elapsed cooldown, so the
    // resend stays gated until the read settles — ready is the failure answer.
    isResolving: !deadline && !hasFailedRead,
  };
};

export const useResendCountdown = (nextResendAt?: string) => {
  const deadline = parseDeadline(nextResendAt);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      if (readCountdown(deadline) <= READY) {
        return () => undefined;
      }

      const interval = setInterval(() => {
        onStoreChange();

        if (readCountdown(deadline) <= READY) {
          clearInterval(interval);
        }
      }, MS_PER_SECOND);

      return () => clearInterval(interval);
    },
    [deadline]
  );

  const getSnapshot = useCallback(() => readCountdown(deadline), [deadline]);
  const countdown = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  const secondsRemaining = Math.max(countdown, READY);

  return {
    canResend: countdown === READY,
    isExhausted: countdown === EXHAUSTED,
    secondsRemaining,
    formattedTime: formatTime(secondsRemaining),
  };
};
